import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Popover, Tag } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { getLang } from 'shared/store/selectors/lang.selector';
import { usePopoverFocus } from '../../design-system/popover/hooks/use-popover-focus';
import { Button } from '../../design-system';
import { IconButton } from '../../design-system/buttons';
import { formatNumber } from 'shared/utils';
import styles from './email-list-filter.module.scss';

function parseEmailList(text) {
  if (!text?.trim()) return [];
  return [
    ...new Set(
      text
        .split(/[,;\n\r]+/)
        .map(e => e.trim().toLowerCase())
        .filter(e => e.length > 0),
    ),
  ];
}

export function EmailListFilter({ emailFilterList, onApply, onClear }) {
  const lang = useSelector(getLang('USERS_MANAGEMENT'));
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const buttonRef = useRef(null);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const textareaRef = useRef(null);

  const getPopupContainer = useCallback(() => containerRef.current, []);

  const handleToggle = useCallback(() => {
    setOpen(prev => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    buttonRef.current?.focus();
  }, []);

  usePopoverFocus(contentRef, handleClose, open);

  const handleOpenChange = useCallback(newOpen => {
    if (!newOpen) {
      setOpen(false);
    }
  }, []);

  const handleApply = useCallback(() => {
    const emails = parseEmailList(inputValue);
    onApply(emails);
    handleClose();
  }, [inputValue, onApply, handleClose]);

  const handleClear = useCallback(() => {
    setInputValue('');
    onClear();
    handleClose();
  }, [onClear, handleClose]);

  // Focus textarea when popover opens
  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
  }, [open]);

  // Sync inputValue when popover opens with existing filter
  useEffect(() => {
    if (open && emailFilterList.length > 0) {
      setInputValue(emailFilterList.join('\n'));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const popoverContent = (
    <div ref={contentRef} className={styles.popoverContent}>
      <textarea
        ref={textareaRef}
        className={styles.textarea}
        rows={6}
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        placeholder={lang.EMAIL_LIST_FILTER_PLACEHOLDER}
      />
      <div className={styles.actions}>
        <Button type="primary" size="small" onClick={handleApply} disabled={!inputValue.trim()}>
          {lang.EMAIL_LIST_FILTER_APPLY}
        </Button>
        <Button
          size="small"
          onClick={handleClear}
          disabled={emailFilterList.length === 0 && !inputValue.trim()}>
          {lang.EMAIL_LIST_FILTER_CLEAR}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <div ref={containerRef} className={styles.container}>
        <Popover
          content={popoverContent}
          open={open}
          onOpenChange={handleOpenChange}
          trigger="click"
          placement="bottomRight"
          getPopupContainer={getPopupContainer}>
          <IconButton
            ref={buttonRef}
            label={lang.EMAIL_LIST_FILTER}
            onClick={handleToggle}
            className={classNames(styles.trigger, {
              [styles.triggerActive]: emailFilterList.length > 0,
            })}>
            <FontAwesomeIcon icon={faUsers} />
          </IconButton>
        </Popover>
      </div>
      {emailFilterList.length > 0 && (
        <div className={styles.indicator}>
          <Tag
            className={styles.filterTag}
            closable
            onClose={handleClear}
            closeIcon={
              <button className={styles.closeIcon} aria-label={lang.EMAIL_LIST_FILTER_REMOVE}>
                <FontAwesomeIcon className={styles.icon} icon={faXmark} size="lg" />
              </button>
            }>
            {lang.EMAIL_LIST_FILTER_ACTIVE.replace('{count}', formatNumber(emailFilterList.length))}
          </Tag>
        </div>
      )}
    </>
  );
}
